import React, { useMemo } from "react";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { CurrencyEnums, StyleEnums } from "models/enums";
import { AutomationRuleListItem, AutomationRuleListProps } from "models/propTypes/automationRulePropTypes";
import { TableHandles } from "models/propTypes/simpleTableProps";
import { Amount } from "models/utilTypes/currencyType";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import {
  WintSuperkollAPIModelCustomerAutomationsCommon as AutomationAPIModel,
  WintUtilsWebApi,
} from "wint-api-client/dist/types";
import { AutomationRuleType } from "models/enums/automationRule";
//#endregion
//#region components
import { Icon, TableAsync, Tags, useFilterContext } from "components/common";
import { useCreateAutomationRuleDialog } from "components/automationRule";
import { useAutomationRuleService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { AutomationRuleTypeHelper } from "utils/helpers";
import ListMoreActionButton from "./moreActions/moreActionButton";

const AutomationRuleList: React.ForwardRefRenderFunction<TableHandles, AutomationRuleListProps> = (props, ref) => {
  const automationService = useAutomationRuleService();

  const dispatch = useDispatch();

  const { reloadKey, reloadList } = props;

  const allDimensionsMap = useSelector((store: AppState) => store.common.dimensions.allDimensions);
  const allDimentions = useMemo(() => allDimensionsMap?.map((d) => d.value)?.flat() ?? [], [allDimensionsMap]);
  const getDimensions = (dimensionIds: string[]) => {
    return (
      allDimentions?.filter((c) => c.id !== undefined && dimensionIds.includes(c.id)).map((d) => d.name ?? "") ?? []
    );
  };

  const { filter } = useFilterContext<AutomationAPIModel.AutomationRuleFilter>();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) =>
      automationService.reportFilter(baseFilter, filter).then((res) => automationService.convertReportToListItems(res)),
    [filter]
  );

  const openEditRuleDialog = useCreateAutomationRuleDialog();
  const onRowClick = (row: AutomationAPIModel.AutomationRuleListItem, rowIndex: number) => {
    if (
      AutomationRuleTypeHelper.getRuleTypeEnumByApiTypeAndSubType(row.ruleType, row.ruleSubtype) ===
      AutomationRuleType.Unknown
    )
      return;
    dispatch(
      displayDialog(
        openEditRuleDialog(
          row.ruleId,
          AutomationRuleTypeHelper.getRuleTypeEnumByApiTypeAndSubType(row.ruleType, row.ruleSubtype),
          reloadList
        )
      )
    );
  };

  return (
    <TableAsync<AutomationRuleListItem>
      tableRef={ref}
      properties={{
        ruleType: {
          description: TranslationKeys.Common.Type,
          formatter: (r: AutomationAPIModel.AutomationRuleListItem) =>
            AutomationRuleTypeHelper.getRuleTypeTextByApiTypeAndSubType(r.ruleType, r.ruleSubtype),
        },
        totalEntitiesProcessed: {
          description: TranslationKeys.AutomationRule.AutomatedEvents,
          alignment: "right",
        },
        createdDate: {
          description: TranslationKeys.AutomationRule.CreateDate,
          formatter: (r: AutomationAPIModel.AutomationRuleListItem) => new Date(r.createdDate),
        },
        concerns: {
          description: TranslationKeys.AutomationRule.Concerns,
          disableSort: true,
        },
        counterparty: {
          description: TranslationKeys.AutomationRule.Counterparty,
          disableSort: true,
        },
        maxAmount: {
          description: TranslationKeys.AutomationRule.MaxAmount,
          formatter: (r: AutomationAPIModel.AutomationRuleListItem) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, r.maxAmount),
          alignment: "right",
        },
        dimensions: {
          description: TranslationKeys.Common.Tags,
          formatter: (r: AutomationAPIModel.AutomationRuleListItem) => (
            <Tags tags={r.dimensions ? getDimensions(r.dimensions) : []} maxDisplayCount={1} />
          ),
          disableSort: true,
        },
        isActive: {
          description: TranslationKeys.Common.Active,
          formatter: (r: AutomationAPIModel.AutomationRuleListItem) =>
            r.isActive ? (
              <Icon
                icon={IconDefinitionConstants.WValidateCheck}
                className={cssExports.tableIcon}
                displayType={StyleEnums.DisplayType.Success}
              />
            ) : (
              <></>
            ),
          alignment: "center",
        },
        moreActionColumn: {
          formatter: (r: AutomationAPIModel.AutomationRuleListItem) => (
            <ListMoreActionButton row={r} onActionSuccess={reloadList} />
          ),
          alignment: "left",
          disableSort: true,
        },
      }}
      reloadKey={reloadKey}
      filter={filterFunc}
      rowClickHandler={onRowClick}
      maxRowCount={10}
      orderByProperty={"createdDate"}
      orderByDescending
      pagination="advanced"
    />
  );
};

export default React.forwardRef(AutomationRuleList);
